import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { fetchTradeCareerData, stateName, defaultPayTrade } from '../utils/tradePayData';
import TradePayDetailCard from './TradePayDetailCard';
import PopularTradeTiles from './PopularTradeTiles';
import TradeQuiz from './TradeQuiz';
import useIsPhone from '../utils/useIsPhone';

const NAVY_MID  = '#132233';
const AMBER     = '#F5A623';
const WHITE     = '#FFFFFF';
const FONT_HEAD = "'Bebas Neue', sans-serif";
const FONT_BODY = "'DM Sans', sans-serif";

function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < breakpoint);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [breakpoint]);
  return isMobile;
}

const selStyle = {
  padding: '9px 12px', fontSize: '0.84rem', backgroundColor: NAVY_MID,
  border: '1px solid rgba(255,255,255,0.12)', borderRadius: '8px',
  color: WHITE, outline: 'none', cursor: 'pointer', fontFamily: FONT_BODY,
};

// initialTrade: optional deep-link (e.g. from a School/Union Finder pay snippet) -- when
// set, selects that trade (and its state) as soon as data loads, then calls
// onInitialTradeApplied() once so a later visit to this tab without a fresh deep-link
// falls back to normal default-selection instead of "sticking" on the old trade.
// onViewTradeHub: optional -- forwarded to TradePayDetailCard so its "View full trade guide"
// link only appears when the parent actually supports opening the Trade Hub.
const MAX_COMPARE = 3;

// Phone (<= 640px) overrides: 44px tap targets, 16px form fields (stops iOS zoom-on-focus),
// body text at least 14px, lighter helper text. Desktop/tablet styles are unchanged.
const PHONE_TEXT = '0.875rem';
const PHONE_HELPER = 'rgba(255,255,255,0.68)';
const PHONE_FIELD = { minHeight: '44px', fontSize: '16px' };
const PHONE_BTN = { minHeight: '44px', fontSize: '0.9rem' };

export default function CareerPayGuide({ initialTrade, onInitialTradeApplied, onViewTradeHub, initialQuizOpen = false }) {
  const { t } = useTranslation();
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [state, setState]     = useState('');
  const [trade, setTrade]     = useState('');
  const [search, setSearch]   = useState('');
  const [tradeSort, setTradeSort] = useState('name');
  const [compareTrades, setCompareTrades] = useState([]);
  const [compareMode, setCompareMode]     = useState(false);
  const isMobile = useIsMobile();
  const isPhone = useIsPhone();
  const detailRef = useRef(null);
  const [scrollToDetail, setScrollToDetail] = useState(0);
  // Phones: the compare controls live in a bar fixed to the bottom of the screen (see
  // CompareBar below) instead of the tray above the list. Its measured height pads the page
  // so the bar never covers the end of the list or the trade card.
  const showCompareBar = isPhone && compareTrades.length > 0;
  const compareBarRef = useRef(null);
  const [compareBarHeight, setCompareBarHeight] = useState(0);
  useEffect(() => {
    const el = compareBarRef.current;
    if (!showCompareBar || !el) { setCompareBarHeight(0); return undefined; }
    const measure = () => setCompareBarHeight(el.getBoundingClientRect().height);
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [showCompareBar]);
  const clearCompare = () => { setCompareTrades([]); setCompareMode(false); };

  // Compare view: line up the union scale blocks of the columns that share a row, when every one
  // of them has a block (each block loads on its own, and the content above it differs per trade).
  // Measured after layout and re-measured whenever a column changes size.
  const compareGridRef = useRef(null);
  const [unionOffsets, setUnionOffsets] = useState({});
  useEffect(() => {
    const grid = compareGridRef.current;
    if (!compareMode || !grid) { setUnionOffsets(prev => (Object.keys(prev).length ? {} : prev)); return undefined; }
    const measure = () => {
      const cols = [...grid.querySelectorAll('[data-compare-col]')];
      const rowsByTop = new Map();
      for (const col of cols) {
        const top = Math.round(col.getBoundingClientRect().top);
        if (!rowsByTop.has(top)) rowsByTop.set(top, []);
        rowsByTop.get(top).push(col);
      }
      const next = {};
      for (const group of rowsByTop.values()) {
        const slots = group.map(col => col.querySelector('[data-union-slot]'));
        if (group.length < 2 || slots.some(sl => !sl || !sl.querySelector('[data-union-scale]'))) continue;
        // Natural position of each block = where it is now minus the offset already applied above it
        // (the offset is padding inside the slot, so it moves the block, not the slot).
        const natural = slots.map(sl => sl.querySelector('[data-union-scale]').getBoundingClientRect().top - (parseFloat(sl.style.paddingTop) || 0));
        const target = Math.max(...natural);
        group.forEach((col, i) => { const off = Math.round(target - natural[i]); if (off > 0) next[col.dataset.compareCol] = off; });
      }
      setUnionOffsets(prev => {
        const a = Object.entries(prev), b = Object.entries(next);
        return a.length === b.length && b.every(([k, v]) => prev[k] === v) ? prev : next;
      });
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(grid);
    grid.querySelectorAll('[data-compare-col]').forEach(col => ro.observe(col));
    return () => ro.disconnect();
  }, [compareMode, compareTrades.join('|')]); // eslint-disable-line

  const toggleCompare = (tradeName) => {
    setCompareTrades(prev => {
      if (prev.includes(tradeName)) {
        const next = prev.filter(t => t !== tradeName);
        if (next.length === 0) setCompareMode(false);
        return next;
      }
      if (prev.length >= MAX_COMPARE) return prev;
      return [...prev, tradeName];
    });
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const data = await fetchTradeCareerData();
      if (cancelled) return;
      setRows(data);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  const states = useMemo(() => [...new Set(rows.map(r => r.state))].sort(), [rows]);

  // Keep `state` valid as data loads/changes, defaulting to the first available state
  // rather than ever hardcoding 'IL' -- more states land in this table incrementally.
  useEffect(() => {
    if (states.length === 0) return;
    if (!states.includes(state)) setState(states[0]);
  }, [states]); // eslint-disable-line

  const tradesForState = useMemo(() => {
    const list = rows.filter(r => r.state === state);
    return list.sort((a, b) => {
      // median_pay is the one figure every trade is guaranteed to have (HVAC Technician's
      // null entry/top don't affect this at all -- it sorts on median_pay same as any
      // other row). Nulls (none exist today) sort to the bottom of either direction.
      if (tradeSort === 'pay_desc') return (b.median_pay ?? -Infinity) - (a.median_pay ?? -Infinity);
      if (tradeSort === 'pay_asc')  return (a.median_pay ?? Infinity) - (b.median_pay ?? Infinity);
      return a.trade.localeCompare(b.trade);
    });
  }, [rows, state, tradeSort]);

  useEffect(() => {
    if (tradesForState.length === 0) { setTrade(''); return; }
    if (!tradesForState.some(r => r.trade === trade)) setTrade(defaultPayTrade(tradesForState));
  }, [tradesForState]); // eslint-disable-line

  // A popular-trade tile was tapped: bring the detail card into view once it has re-rendered.
  // In the stacked (phone/tablet) layout the card sits under the trade list, so scroll it to
  // the top; side by side, only scroll if it's out of view. The scroll margin clears the
  // sticky site header.
  useEffect(() => {
    if (!scrollToDetail || !detailRef.current) return;
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    detailRef.current.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: isMobile ? 'start' : 'nearest' });
  }, [scrollToDetail]); // eslint-disable-line

  // Deep-link from a pay snippet: once rows are loaded, jump straight to the requested
  // trade (and its state, in case that ever differs from whatever got auto-selected above),
  // then tell the parent it's been consumed so it doesn't keep re-applying on later visits.
  useEffect(() => {
    if (!initialTrade || rows.length === 0) return;
    const match = rows.find(r => r.trade === initialTrade);
    if (match) { setState(match.state); setTrade(match.trade); setCompareMode(false); setScrollToDetail(n => n + 1); }
    onInitialTradeApplied?.();
  }, [initialTrade, rows]); // eslint-disable-line

  // A Popular trades tile or a quiz result was tapped: select that trade and scroll its card into view.
  const selectFromTop = name => { setTrade(name); setCompareMode(false); setScrollToDetail(n => n + 1); };
  // Quiz with no qualifying trades: "Browse all trades" closes it and brings the trade list into view.
  const browseRef = useRef(null);
  const browseAll = () => requestAnimationFrame(() => {
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    browseRef.current?.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
  });

  const filteredTrades = tradesForState.filter(r => r.trade.toLowerCase().includes(search.toLowerCase()));
  const selected = tradesForState.find(r => r.trade === trade) || null;
  const compareRows = compareTrades.map(t => tradesForState.find(r => r.trade === t)).filter(Boolean);

  return (
    <div style={showCompareBar ? { paddingBottom: `${Math.ceil(compareBarHeight) + 16}px` } : undefined}>
      <h1 style={{ fontFamily: FONT_HEAD, fontSize: '1.7rem', color: WHITE, letterSpacing: '0.04em', marginBottom: '6px' }}>
        {t('resources.payGuide.title')}
      </h1>
      <p style={{ fontSize: isPhone ? PHONE_TEXT : '0.8rem', color: isPhone ? PHONE_HELPER : 'rgba(255,255,255,0.45)', lineHeight: 1.5, marginBottom: '16px', maxWidth: '640px' }}>
        {t('resources.payGuide.subtitle')}
      </p>

      {!loading && tradesForState.length > 0 && (
        <TradeQuiz rowsForState={tradesForState} onSelectTrade={selectFromTop} onBrowseAll={browseAll} initialOpen={initialQuizOpen} />
      )}

      {!loading && (
        <PopularTradeTiles
          rowsForState={tradesForState}
          selectedTrade={compareMode ? '' : trade}
          onSelect={selectFromTop}
        />
      )}

      {/* Union Wages callout -- sits above the trade filters so it's seen before browsing. */}
      <div data-union-wages-card style={{
        display: 'flex', alignItems: isMobile ? 'stretch' : 'center', gap: '12px 16px',
        flexDirection: isMobile ? 'column' : 'row',
        backgroundColor: NAVY_MID, border: '1px solid rgba(255,255,255,0.07)', borderRadius: '12px',
        padding: '14px 16px', marginBottom: '16px',
      }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ fontFamily: FONT_HEAD, fontSize: '1.15rem', color: WHITE, letterSpacing: '0.04em', marginBottom: '2px' }}>
            {t('resources.payGuide.unionWagesCardTitle')}
          </p>
          <p style={{ fontSize: isPhone ? PHONE_TEXT : '0.8rem', color: isPhone ? PHONE_HELPER : 'rgba(255,255,255,0.55)', lineHeight: 1.5 }}>
            {t('resources.payGuide.unionWagesCardBody')}
          </p>
        </div>
        <Link
          to="/union-wages"
          style={{
            backgroundColor: AMBER, color: NAVY_MID, fontWeight: 700, fontFamily: FONT_BODY, fontSize: '0.8rem',
            borderRadius: '7px', padding: '9px 16px', textDecoration: 'none', textAlign: 'center', whiteSpace: 'nowrap', flexShrink: 0,
            ...(isPhone ? { ...PHONE_BTN, display: 'flex', alignItems: 'center', justifyContent: 'center' } : {}),
          }}
        >
          {t('resources.payGuide.unionWagesCardButton')}
        </Link>
      </div>

      {/* State selector -- only one option today, but driven entirely by whatever states
          actually exist in the data, not a hardcoded "Illinois". */}
      <div ref={browseRef} data-trade-browse style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '16px', scrollMarginTop: '80px' }}>
        <select value={state} onChange={e => setState(e.target.value)} style={{ ...selStyle, ...(isPhone ? PHONE_FIELD : {}) }}>
          {states.map(s => <option key={s} value={s}>{stateName(s)}</option>)}
        </select>
        <input
          placeholder={t('resources.payGuide.searchPlaceholder')}
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ ...selStyle, cursor: 'text', flex: isMobile ? '1 1 100%' : '0 1 240px', minWidth: '160px', ...(isPhone ? PHONE_FIELD : {}) }}
        />
        <select value={tradeSort} onChange={e => setTradeSort(e.target.value)} style={{ ...selStyle, flex: isMobile ? '1 1 100%' : undefined, ...(isPhone ? PHONE_FIELD : {}) }}>
          <option value="name">{t('resources.sortNameAZ')}</option>
          <option value="pay_desc">{t('resources.payGuide.sortPayDesc')}</option>
          <option value="pay_asc">{t('resources.payGuide.sortPayAsc')}</option>
        </select>
      </div>

      {/* Compare tray -- appears once at least one trade is checked in the list below.
          Stays visible while compareMode is active too, so the same bar doubles as the
          "return to normal browsing" exit and the entry point back into compare.
          Phones use the fixed bottom bar instead (rendered at the end of this component). */}
      {compareTrades.length > 0 && !isPhone && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap',
          backgroundColor: 'rgba(245,166,35,0.08)', border: '1px solid rgba(245,166,35,0.3)',
          borderRadius: '10px', padding: '10px 14px', marginBottom: '16px',
        }}>
          <span style={{ fontSize: isPhone ? PHONE_TEXT : '0.82rem', color: WHITE, fontWeight: 600 }}>
            {t('resources.payGuide.compareTraySelected', { count: compareTrades.length, max: MAX_COMPARE })}
          </span>
          <div style={{ display: 'flex', gap: '8px', marginLeft: isMobile ? 0 : 'auto', flexWrap: 'wrap' }}>
            {!compareMode ? (
              <button
                onClick={() => setCompareMode(true)}
                disabled={compareTrades.length < 2}
                style={{
                  backgroundColor: compareTrades.length < 2 ? 'rgba(245,166,35,0.3)' : AMBER,
                  color: NAVY_MID, fontWeight: 700, fontFamily: FONT_BODY, fontSize: '0.78rem',
                  border: 'none', borderRadius: '7px', padding: '7px 14px',
                  cursor: compareTrades.length < 2 ? 'not-allowed' : 'pointer',
                  ...(isPhone ? PHONE_BTN : {}),
                }}
              >
                {t('resources.payGuide.compareButton', { count: compareTrades.length })}
              </button>
            ) : (
              <button
                onClick={() => setCompareMode(false)}
                style={{
                  backgroundColor: 'transparent', color: WHITE, fontWeight: 700, fontFamily: FONT_BODY,
                  fontSize: '0.78rem', border: '1px solid rgba(255,255,255,0.3)', borderRadius: '7px',
                  padding: '7px 14px', cursor: 'pointer',
                  ...(isPhone ? PHONE_BTN : {}),
                }}
              >
                {t('resources.payGuide.backToSingle')}
              </button>
            )}
            <button
              onClick={clearCompare}
              style={{
                backgroundColor: 'transparent', color: 'rgba(255,255,255,0.6)', fontFamily: FONT_BODY,
                fontSize: '0.78rem', border: 'none', padding: '7px 4px', cursor: 'pointer', textDecoration: 'underline',
                ...(isPhone ? { ...PHONE_BTN, padding: '0 10px' } : {}),
              }}
            >
              {t('common.clear')}
            </button>
          </div>
        </div>
      )}

      {loading && (
        <div style={{ textAlign: 'center', padding: '2.5rem 0', color: 'rgba(255,255,255,0.3)', fontSize: '0.9rem' }}>
          {t('common.loading')}
        </div>
      )}

      {!loading && rows.length === 0 && (
        <div style={{ textAlign: 'center', padding: '2.5rem 0', color: 'rgba(255,255,255,0.3)', fontSize: '0.9rem' }}>
          {t('resources.payGuide.noData')}
        </div>
      )}

      {!loading && rows.length > 0 && (
        <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start', flexDirection: isMobile ? 'column' : 'row' }}>
          {/* Trade list -- wide enough that names wrap rather than get cut off, with a thin dark
              scrollbar (scrollbarColor/scrollbarWidth are the inline-style way to theme it). The
              header row labels the checkbox column: ticking trades adds them to Compare. */}
          <div style={{
            width: isMobile ? '100%' : '320px', flexShrink: 0,
            backgroundColor: NAVY_MID, border: '1px solid rgba(255,255,255,0.07)', borderRadius: '12px',
            maxHeight: isMobile ? '280px' : '520px', overflowY: 'auto',
            scrollbarWidth: 'thin', scrollbarColor: 'rgba(255,255,255,0.22) transparent',
          }}>
            <div style={{
              position: 'sticky', top: 0, zIndex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px',
              padding: '8px 12px 8px 14px', backgroundColor: NAVY_MID, borderBottom: '1px solid rgba(255,255,255,0.07)',
              fontSize: isPhone ? '0.75rem' : '0.62rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: isPhone ? PHONE_HELPER : 'rgba(255,255,255,0.4)',
            }}>
              <span>{t('resources.payGuide.listTradeHeader')}</span>
              <span title={t('resources.payGuide.compareUpTo', { max: MAX_COMPARE })}>{t('resources.payGuide.listCompareHeader', { max: MAX_COMPARE })}</span>
            </div>
            {filteredTrades.length === 0 && (
              <div style={{ padding: '16px', fontSize: isPhone ? PHONE_TEXT : '0.82rem', color: isPhone ? PHONE_HELPER : 'rgba(255,255,255,0.35)' }}>
                {t('resources.noTradesMatch')}
              </div>
            )}
            {filteredTrades.map(r => {
              const isActive = r.trade === trade && !compareMode;
              const isChecked = compareTrades.includes(r.trade);
              const checkboxDisabled = !isChecked && compareTrades.length >= MAX_COMPARE;
              return (
                <div
                  key={r.trade}
                  style={{
                    display: 'flex', alignItems: 'center',
                    background: isActive ? 'rgba(245,166,35,0.12)' : 'transparent',
                    borderLeft: isActive ? `3px solid ${AMBER}` : '3px solid transparent',
                  }}
                >
                  <button
                    onClick={() => { setTrade(r.trade); setCompareMode(false); }}
                    style={{
                      flex: 1, minWidth: 0, display: 'block', width: '100%', textAlign: 'left',
                      background: 'none', border: 'none',
                      padding: '10px 8px 10px 14px', fontSize: '0.84rem', fontFamily: FONT_BODY,
                      color: isActive ? AMBER : 'rgba(255,255,255,0.75)',
                      fontWeight: isActive ? 700 : 500, cursor: 'pointer', lineHeight: 1.35,
                      overflowWrap: 'anywhere',
                      ...(isPhone ? { minHeight: '48px', fontSize: '0.95rem' } : {}),
                    }}
                  >
                    {r.trade}
                  </button>
                  <label
                    title={checkboxDisabled ? t('resources.payGuide.compareUpTo', { max: MAX_COMPARE }) : t('resources.payGuide.addToCompare')}
                    style={{
                      display: 'flex', alignItems: 'center', padding: '10px 12px 10px 4px',
                      cursor: checkboxDisabled ? 'not-allowed' : 'pointer', flexShrink: 0,
                      ...(isPhone ? { minWidth: '48px', minHeight: '48px', padding: 0, justifyContent: 'center' } : {}),
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={isChecked}
                      disabled={checkboxDisabled}
                      onChange={() => toggleCompare(r.trade)}
                      aria-label={t('resources.payGuide.compareTradeAria', { trade: r.trade })}
                      style={{ cursor: checkboxDisabled ? 'not-allowed' : 'pointer', accentColor: AMBER, ...(isPhone ? { width: '22px', height: '22px', margin: 0 } : {}) }}
                    />
                  </label>
                </div>
              );
            })}
          </div>

          {/* Detail panel -- either the normal single-trade card, or (when compareMode is
              on) a grid of the same card for every checked trade. Forcing isMobile=true on
              each compare card keeps its type sizing compact enough for a narrow column,
              regardless of the real viewport -- the grid itself is what collapses to one
              column on an actually-narrow screen. */}
          <div ref={detailRef} data-pay-detail style={{ flex: 1, minWidth: 0, width: '100%', scrollMarginTop: '80px' }}>
            {compareMode && compareRows.length > 0 ? (
              <div>
                <p style={{ fontFamily: FONT_HEAD, fontSize: '1.1rem', color: WHITE, letterSpacing: '0.03em', marginBottom: '12px' }}>
                  {t('resources.payGuide.comparingTrades', { count: compareRows.length })}
                </p>
                {/* Columns are at least 190px wide; when the panel can't fit one per trade, they
                    wrap onto another row rather than squeezing the card content. */}
                <div ref={compareGridRef} style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? 'minmax(0, 1fr)' : 'repeat(auto-fit, minmax(190px, 1fr))',
                  gap: '16px', alignItems: 'start',
                }}>
                  {compareRows.map(r => (
                    <div key={r.trade} data-compare-col={r.trade} style={{ minWidth: 0 }}>
                      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '6px' }}>
                        <button
                          onClick={() => toggleCompare(r.trade)}
                          style={{
                            fontSize: '0.72rem', color: 'rgba(255,255,255,0.55)', fontFamily: FONT_BODY,
                            background: 'none', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '6px',
                            padding: '4px 9px', cursor: 'pointer',
                            ...(isPhone ? { ...PHONE_BTN, padding: '0 14px' } : {}),
                          }}
                        >
                          {t('resources.payGuide.removeCompare')}
                        </button>
                      </div>
                      <TradePayDetailCard row={r} isMobile={true} compact unionBlockOffset={unionOffsets[r.trade] || 0} onViewTradeHub={onViewTradeHub} />
                    </div>
                  ))}
                </div>
              </div>
            ) : !selected ? (
              <div style={{ textAlign: 'center', padding: '2.5rem 0', color: 'rgba(255,255,255,0.3)', fontSize: '0.9rem' }}>
                {t('resources.payGuide.selectPrompt')}
              </div>
            ) : (
              <TradePayDetailCard row={selected} isMobile={isMobile} onViewTradeHub={onViewTradeHub} />
            )}
          </div>
        </div>
      )}

      {showCompareBar && (
        <CompareBar
          barRef={compareBarRef}
          count={compareTrades.length}
          compareMode={compareMode}
          onCompare={() => { setCompareMode(true); setScrollToDetail(n => n + 1); }}
          onBack={() => setCompareMode(false)}
          onClear={clearCompare}
        />
      )}
    </div>
  );
}

// Phones only: compare controls fixed to the bottom of the screen while at least one trade is
// checked. Before comparing it shows the count, a Compare button (enabled from 2 trades, with a
// hint until then) and Clear; while compare results are showing it becomes "Comparing N trades"
// with Back and Clear, so the way out of compare mode is always in reach. Clears the iPhone home
// indicator via the bottom safe-area inset (the page sets viewport-fit=cover).
function CompareBar({ barRef, count, compareMode, onCompare, onBack, onClear }) {
  const { t } = useTranslation();
  const canCompare = count >= 2;
  const btn = {
    minHeight: '44px', padding: '0 16px', borderRadius: '8px', fontFamily: FONT_BODY,
    fontSize: '0.95rem', fontWeight: 700, cursor: 'pointer', flexShrink: 0,
  };
  return (
    <div
      ref={barRef}
      data-compare-bar
      role="region"
      aria-label={t('resources.payGuide.compareBarLabel')}
      style={{
        position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 90,
        backgroundColor: NAVY_MID, borderTop: '1px solid rgba(245,166,35,0.45)',
        boxShadow: '0 -6px 18px rgba(0,0,0,0.35)',
        padding: '10px 16px calc(10px + env(safe-area-inset-bottom, 0px))',
        display: 'flex', alignItems: 'center', gap: '10px',
      }}
    >
      <div aria-live="polite" style={{ flex: 1, minWidth: 0 }}>
        {compareMode ? (
          <p style={{ fontFamily: FONT_HEAD, fontSize: '1.1rem', color: WHITE, letterSpacing: '0.03em', lineHeight: 1.2 }}>
            {t('resources.payGuide.comparingTrades', { count })}
          </p>
        ) : (
          <>
            <p style={{ fontSize: '0.95rem', fontWeight: 700, color: WHITE, lineHeight: 1.3 }}>
              {t('resources.payGuide.compareBarCount', { count, max: MAX_COMPARE })}
            </p>
            {!canCompare && (
              <p style={{ fontSize: PHONE_TEXT, color: PHONE_HELPER, lineHeight: 1.3 }}>
                {t('resources.payGuide.compareBarPickOne')}
              </p>
            )}
          </>
        )}
      </div>
      <button
        type="button"
        onClick={onClear}
        style={{ ...btn, padding: '0 12px', backgroundColor: 'transparent', color: 'rgba(255,255,255,0.75)', border: 'none', textDecoration: 'underline', textUnderlineOffset: '3px' }}
      >
        {t('common.clear')}
      </button>
      {compareMode ? (
        <button
          type="button"
          onClick={onBack}
          style={{ ...btn, backgroundColor: 'transparent', color: WHITE, border: '1px solid rgba(255,255,255,0.35)' }}
        >
          {t('resources.payGuide.compareBarBack')}
        </button>
      ) : (
        <button
          type="button"
          onClick={onCompare}
          disabled={!canCompare}
          style={{
            ...btn, border: 'none', color: NAVY_MID,
            backgroundColor: canCompare ? AMBER : 'rgba(245,166,35,0.35)',
            cursor: canCompare ? 'pointer' : 'not-allowed',
          }}
        >
          {t('resources.payGuide.compareBarButton')}
        </button>
      )}
    </div>
  );
}
